// Native (Capacitor) push + shell helpers. Every export is a no-op on the web/PWA
// build — guarded by Capacitor.isNativePlatform() — so the same bundle runs everywhere.
import { Capacitor } from "@capacitor/core";
import { hideSplash } from "@/lib/splash";
import { api } from "@/lib/api";
import { runNativePrompt, nativePromptsBusy, nativePromptsHeld, lastNativePromptEndedAt } from "@/lib/nativePrompts";
import { primerEnabled, primerSnoozed, PUSH_PRIMER_EVENT } from "@/lib/pushPrimer";

let listenersInstalled = false;
let listenersInstalled_deeplink = false;
let requesting = null;
let primerSettings = null;
// Jamais de dialogue notifications moins de 60 s après un autre dialogue système (ATT / UMP).
export const PUSH_MIN_GAP_MS = 60_000;

const platform = () => (Capacitor.getPlatform() === "ios" ? "ios" : "android");
const isIos = () => Capacitor.getPlatform() === "ios";
const undecided = (perm) => perm?.receive === "prompt" || perm?.receive === "prompt-with-rationale";
// Réglages admin (settings.push_primer) : posés par App.js dès la config, et par le boot (AppContext) avant le 1er appel.
export const setPushPrimerSettings = (settings) => { if (settings) primerSettings = settings; };
// iOS : toujours différé au contexte naturel ; Android : seulement si la carte est activée en admin (sinon flux 1.5.6).
const deferToContext = () => isIos() || primerEnabled(primerSettings, "android");
const TOKEN_KEY = "yalola.push_token"; // this device's FCM/APNs token, so logout detaches only it

const goToLink = (data) => {
  const link = typeof data?.link === "string" && data.link ? data.link : "/notifications";
  try {
    // Stash the target so the React <DeepLinkHandler/> applies it once the app is ready
    // (auth resolved + router mounted). On a cold start the tap can arrive before React
    // boots — pushState()+popstate would then be lost / overridden by the auth redirect.
    window.__yalolaPendingDeepLink = link;
    try { sessionStorage.setItem("yalola.pendingDeepLink", link); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent("yalola:navigate", { detail: link }));
  } catch { /* noop */ }
};

// Hide the native splash screen + theme the status bar once the web view is ready.
export async function initNative() {
  if (!Capacitor.isNativePlatform()) return;
  // Deep-link taps from our custom Android notification service arrive as a window event.
  if (!listenersInstalled_deeplink) {
    listenersInstalled_deeplink = true;
    window.addEventListener("yalolaPushLink", (e) => {
      const link = e && typeof e.detail === "string" ? e.detail : "";
      if (link) goToLink({ link });
    });
  }
  try {
    // Capacitor 8 SystemBars (bundled with core) replaces the legacy StatusBar
    // plugin, whose Android implementation used APIs deprecated by Android 15+
    // (setStatusBarColor…) and flagged by Google Play. Edge-to-edge is enforced
    // on targetSdk 35/36 and handled natively (MainActivity injects the real
    // system-bar insets as CSS vars), so only the icon style is set here:
    // dark icons on the light header / light icons in dark mode.
    const { SystemBars, SystemBarsStyle } = await import("@capacitor/core");
    const dark = document.documentElement.classList.contains("dark");
    await SystemBars.setStyle({ style: dark ? SystemBarsStyle.Dark : SystemBarsStyle.Light });
  } catch { /* noop */ }
  // Lot 702 — masquage centralisé et idempotent (App.js le fait déjà au premier rendu ; ici au cas où il arriverait avant).
  try { await hideSplash(); } catch { /* noop */ }
}

async function installListeners(PushNotifications) {
  if (listenersInstalled) return;
  listenersInstalled = true;
  await PushNotifications.addListener("registration", async (t) => {
    try { localStorage.setItem(TOKEN_KEY, t.value); } catch { /* noop */ }
    try { await api.post("/push/register", { token: t.value, platform: platform() }); } catch { /* noop */ }
  });
  await PushNotifications.addListener("registrationError", (e) => console.error("push reg error", e));
  await PushNotifications.addListener("pushNotificationActionPerformed", (a) => goToLink(a?.notification?.data));
}

// Ask permission, register with FCM/APNs, and send the device token to the backend.
// Fully guarded: push is best-effort and must NEVER break login/startup. On 1.5.4 (11) a stripped
// @CapacitorPlugin annotation made the NATIVE checkPermissions() throw an NPE right after login → the
// real fix is the R8 keep rule (annotation retained) + a native null-guard in Bridge.getPermissionStates;
// this JS layer stays null-safe and swallows any rejection so a push failure can't cascade.
// iOS : SILENCIEUX au boot / à la connexion — permission déjà accordée → token enregistré ; pas encore
// décidée → aucun dialogue ici (voir requestNativePushPermission). Android : inchangé (demande au login),
// sauf si la carte « Activer les notifications ? » est activée pour Android en admin (→ même parcours qu'iOS).
// Lot 732 — Android : Firebase absent dans ce processus (app clonée / « double app », fournisseur non exécuté) →
// PushNotifications.register() plantait l'app (IllegalStateException, Play Console 2.4.3). Le natif répond "0" : on
// saute l'enregistrement (signalé une fois) ; iOS, web et anciennes versions natives (sans la méthode) → inchangé.
let pushEngineReported = false;
export function pushEngineReady() {
  try {
    const fn = window.YalolaInsets?.isPushReady;
    if (typeof fn !== "function") return true;
    const ready = String(fn()) !== "0";
    if (!ready && !pushEngineReported) {
      pushEngineReported = true;
      console.warn("push: moteur Firebase indisponible dans ce processus — enregistrement ignoré");
      try { window.dispatchEvent(new CustomEvent("yalola:push_engine_unavailable")); } catch { /* noop */ }
      // Signal (pas un plantage) visible dans Admin › Plantages : combien d'appareils sont concernés.
      import("@/lib/crashReport").then((m) => m.reportClientCrash({ error: { message: "Firebase indisponible dans ce processus (app clonée / double app ?) — enregistrement push ignoré", stack: "" }, kind: "push_engine", foreground: false })).catch(() => {});
    }
    return ready;
  } catch { return true; }
}
const safeRegister = async (PushNotifications) => { if (pushEngineReady()) await PushNotifications.register(); };

export async function registerNativePush(settings) {
  if (!Capacitor.isNativePlatform()) return;
  setPushPrimerSettings(settings);
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await installListeners(PushNotifications);
    let perm = await PushNotifications.checkPermissions();
    if (undecided(perm)) {
      if (deferToContext()) return;
      // System dialog → queued after ATT / UMP consent so prompts never overlap.
      perm = await runNativePrompt("push", () => PushNotifications.requestPermissions());
    }
    if (!perm || perm.receive !== "granted") return;
    await safeRegister(PushNotifications);
  } catch (e) {
    // Never let a push/permission failure crash or block the app.
    console.error("registerNativePush failed", e);
  }
}

// Contexte naturel atteint (tap sur la cloche, première action sociale — voir NotificationBell.askPush et
// l'intercepteur lib/api.js). Renonce silencieusement (« postponed ») si un dialogue système est en cours ou s'est
// fermé il y a moins de PUSH_MIN_GAP_MS → jamais UMP puis Notifications à la chaîne ; la demande repart au prochain
// contexte. Carte Yalola activée (admin) → on AFFICHE LA CARTE (PushPrimerCard) au lieu du dialogue système
// (« primer ») ; « Plus tard » récent → « snoozed ». Carte désactivée → dialogue système direct.
// No-op web, et Android tant que la carte n'y est pas activée ; un seul appel en vol.
export function requestNativePushPermission() {
  if (!Capacitor.isNativePlatform() || !deferToContext()) return Promise.resolve("unavailable");
  if (requesting) return requesting;
  requesting = (async () => {
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      await installListeners(PushNotifications);
      let perm = await PushNotifications.checkPermissions();
      if (undecided(perm)) {
        // Lot 589 : profil à compléter / feuille ouverte → ni carte ni dialogue par-dessus ; la demande repart au prochain contexte.
        if (nativePromptsBusy() || nativePromptsHeld() || Date.now() - lastNativePromptEndedAt() < PUSH_MIN_GAP_MS) return "postponed";
        if (primerEnabled(primerSettings, Capacitor.getPlatform())) {
          if (primerSnoozed(primerSettings)) return "snoozed";
          window.dispatchEvent(new CustomEvent(PUSH_PRIMER_EVENT));
          return "primer";
        }
        perm = await runNativePrompt("push", () => PushNotifications.requestPermissions());
      }
      if (!perm || perm.receive !== "granted") return perm?.receive || "unknown";
      await safeRegister(PushNotifications);
      return "granted";
    } catch (e) {
      console.error("requestNativePushPermission failed", e);
      return "error";
    } finally {
      requesting = null;
    }
  })();
  return requesting;
}

// Bouton « Activer » de la carte : l'utilisateur a déjà dit oui → dialogue système (file native) puis register.
export async function promptNativePushPermission() {
  if (!Capacitor.isNativePlatform()) return "unavailable";
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await installListeners(PushNotifications);
    let perm = await PushNotifications.checkPermissions();
    if (undecided(perm)) perm = await runNativePrompt("push", () => PushNotifications.requestPermissions());
    if (!perm || perm.receive !== "granted") return perm?.receive || "unknown";
    await safeRegister(PushNotifications);
    return "granted";
  } catch (e) {
    console.error("promptNativePushPermission failed", e);
    return "error";
  }
}

// Logout: detach THIS device's token only (other phones/tablets of the member keep theirs).
export async function deregisterNativePush() {
  if (!Capacitor.isNativePlatform()) return;
  let token = null;
  try { token = localStorage.getItem(TOKEN_KEY); } catch { /* noop */ }
  try { await api.delete("/push/register", { data: { token, platform: platform() } }); } catch { /* noop */ }
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await PushNotifications.removeAllListeners();
    listenersInstalled = false;
  } catch { /* noop */ }
}
